import { Dialog, useMediaQuery, useTheme } from '@mui/material';
import {
  GridPanel,
  useGridApiContext,
  type GridPanelProps,
} from '@mui/x-data-grid-premium';

/**
 * The Data Grid publishes its design tokens as CSS custom properties on a
 * generated class that sits on the grid root, and `GridPanel` re-applies that
 * class to its own popper because the popper is portaled out of the grid. A
 * dialog is portaled the same way, so it needs the class as well — without it
 * the panels lose every `--DataGrid-t-*` colour and spacing value they read.
 */
const GRID_VARIABLES_CLASS_PREFIX = 'MuiDataGridVariables-';

/** `GridPreferencesPanel` also forwards `aria-labelledby`, which `GridPanelProps` omits. */
type EmbeddableGridPanelProps = GridPanelProps & {
  'aria-labelledby'?: string;
};

/**
 * `panel` slot used only by the embeddable widget bundle.
 *
 * The default panel is a Popper anchored to the toolbar button and positioned
 * with `strategy: 'fixed'` (see `embeddablePopperDefaultProps`). Inside a host
 * page that means the panel is placed against the browser viewport rather than
 * the widget, so scrolling drags it away from the grid and over unrelated host
 * content. Rendering it as a Dialog reuses the behaviour the date pickers
 * already have in widgets: `createEmbeddableWidgetTheme` scopes MuiDialog to the
 * host mount node, so the panel is centred in the widget container over a
 * backdrop covering only that container, and it holds its position while the
 * host scrolls.
 *
 * Real mobile viewports keep the popper, because the grids already swap in
 * `XsPreferencesDrawerToolbar` at that width.
 */
export function EmbeddableGridPanel({
  open,
  onClose,
  children,
  ...panelProps
}: EmbeddableGridPanelProps) {
  const apiRef = useGridApiContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <GridPanel
        open={open}
        onClose={onClose}
        {...panelProps}
      >
        {children}
      </GridPanel>
    );
  }

  const variablesClass = Array.from(
    apiRef.current.rootElementRef.current?.classList ?? [],
  ).find((className) => className.startsWith(GRID_VARIABLES_CLASS_PREFIX));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={panelProps['aria-labelledby']}
      slotProps={{
        paper: {
          id: panelProps.id,
          className: variablesClass,
          // Panel content stretches itself with `flex: 1`, and widget tiles can
          // be shorter than a long column list.
          sx: { display: 'flex', overflowY: 'auto' },
        },
      }}
    >
      {children}
    </Dialog>
  );
}
